import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FeatureControlledComponent, Image, ImageProps } from 'UI-Dash-Commons-UI';
import DocsMenuSection from "../types/DocsMenuSection";
import DocsSectionUtils from "../utils/DocsSectionUtils";

const openImage = require( '../images/menu/open.svg' );

type LeftSideMenuSectionComponentProps =
    {
        activeSectionUrl: string,
        handleOnClick: ( newUrl: string ) => void,
        handleOpenSection: ( newSectionUrl: string ) => void,
        level: number,
        menuSection: DocsMenuSection,
        openSectionUrl: string
    }

type LeftSideMenuSectionComponentState =
    {
        isOpen: boolean;
        openSectionUrl: string;
    };

class LeftSideMenuSectionComponent extends React.Component<LeftSideMenuSectionComponentProps, LeftSideMenuSectionComponentState>
{
    constructor( props )
    {
        super( props );

        this.state =
            {
                isOpen: LeftSideMenuSectionComponent.isSubSection( this.props.menuSection.url, this.props.openSectionUrl ),
                openSectionUrl: this.props.openSectionUrl
            };
    }

    static isSubSection: ( sectionUrl: string, openSectionUrl: string ) => boolean =
        (
            sectionUrl: string,
            openSectionUrl: string
        ) =>
        {
            const isSubSection = sectionUrl === openSectionUrl.substr( 0, sectionUrl.length );
            // not a subsection if only diff is trailing hash
            if ( isSubSection && openSectionUrl[ sectionUrl.length ] === '#' )
            {
                return false;
            }

            return isSubSection;
        }

    static getDerivedStateFromProps( nextProps: LeftSideMenuSectionComponentProps, prevState: LeftSideMenuSectionComponentState )
    {
        if ( prevState.openSectionUrl !== nextProps.openSectionUrl )
        {
            return {
                isOpen: LeftSideMenuSectionComponent.isSubSection( nextProps.menuSection.url, nextProps.openSectionUrl ),
                openSectionUrl: nextProps.openSectionUrl
            }
        }

        return null;
    }

    private getSectionHeader: ( isSectionActive: boolean, isSectionOpen: boolean, level: number, menuSection: DocsMenuSection, sectionImage: string | undefined ) => React.ReactNode =
        (
            isSectionActive: boolean,
            isSectionOpen: boolean,
            level: number,
            menuSection: DocsMenuSection,
            sectionImage: string | undefined
        ) =>
        {
            const isLink = this.isLink( menuSection );
            const isPage = this.isPage( menuSection );

            let url: string = menuSection.url;

            if ( !isLink )
            {
                url = '#';
            }

            const menuArrow: string | undefined = DocsSectionUtils.findImage( 'menu_arrow', isSectionOpen );
            return (
                isPage
                    ?
                    <SectionLink
                        onClick={this.handleLinkClick}
                        to={url}
                    >
                        <SectionHeader
                            isActive={isSectionActive}
                            level={level}
                        >
                            <SectionImage
                                alt={menuSection.id}
                                level={level}
                                src={sectionImage}
                            />
                            <SectionTitle
                                isActive={isSectionActive}
                                isLink={isLink}
                                level={level}
                            >
                                {menuSection.displayName}
                            </SectionTitle>
                            <SectionMenuArrow
                                alt={'menu_arrow'}
                                isLink={isLink}
                                level={level}
                                src={menuArrow}
                            />
                        </SectionHeader>
                    </SectionLink>
                    :
                    <SectionExternalLink
                        href={url}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                    >
                        <SectionImage
                            alt={menuSection.id}
                            level={level}
                            src={sectionImage}
                            width={30}
                        />
                        <SectionTitle
                            isActive={isSectionActive}
                            isLink={isLink}
                            level={level}
                        >
                            {menuSection.displayName}

                            <SectionExternalLinkImage
                                alt={menuSection.displayName}
                                src={openImage}
                            />
                        </SectionTitle>
                    </SectionExternalLink>
            );
        };

    private handleLinkClick: () => void = () =>
    {
        if ( this.isLink( this.props.menuSection ) )
        {
            this.props.handleOnClick( this.props.menuSection.url );
        }
        else
        {
            this.props.handleOpenSection( this.props.menuSection.url );

            this.setState( { isOpen: !this.state.isOpen } );
        }
    };

    private isLink: ( menuSection: DocsMenuSection ) => boolean =
        ( menuSection: DocsMenuSection ) => ( menuSection.children.length === 0 );

    private isPage: ( menuSection: DocsMenuSection ) => boolean =
        ( menuSection: DocsMenuSection ) => ( menuSection.isPage );

    render()
    {
        const { activeSectionUrl, level, menuSection } = this.props;

        const isSectionActive = LeftSideMenuSectionComponent.isSubSection( menuSection.url, activeSectionUrl );
        const sectionImage: string | undefined = DocsSectionUtils.findImage( menuSection.id, isSectionActive );

        // Specific hacks for handling lvl 3
        let isForcedOpen = false;
        let adjustedLevel = level;

        if ( level === 3 )
        {
            if ( menuSection.children.length > 0 )
            {
                isForcedOpen = true;
            }
            else
            {
                adjustedLevel = 4;
            }
        }

        const sectionItem = (
            <SectionItem
                isActive={isSectionActive}
                isOpen={isForcedOpen || this.state.isOpen}
                level={adjustedLevel}
            >
                {this.getSectionHeader( isSectionActive, this.state.isOpen, adjustedLevel, menuSection, sectionImage )}

                <SectionChildren
                    isOpen={isForcedOpen || this.state.isOpen}
                    level={adjustedLevel}
                >
                    {
                        ( menuSection.children && menuSection.children.length ) ? menuSection.children.map(
                            ( childMenuSection ) => (
                                <LeftSideMenuSectionComponent
                                    activeSectionUrl={this.props.activeSectionUrl}
                                    handleOnClick={this.props.handleOnClick}
                                    handleOpenSection={this.props.handleOpenSection}
                                    key={childMenuSection.id}
                                    level={adjustedLevel + 1}
                                    menuSection={childMenuSection}
                                    openSectionUrl={this.props.openSectionUrl}
                                />
                            )
                        ) : ''
                    }
                </SectionChildren>
            </SectionItem>
        );

        if ( menuSection.requireFeature )
        {
            return (
                <FeatureControlledComponent
                    requireFeature={menuSection.requireFeature}
                >
                    {sectionItem}
                </FeatureControlledComponent>
            );
        }

        return sectionItem;
    }
}


const SectionItem = styled<{ isActive: boolean, isOpen: boolean, level: number }, 'li'>( 'li' )`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  padding-top: 8px;
  padding-top: ${props => props.level === 0 && '24px'};

  padding-left: 0;
  padding-left: ${props => props.level === 1 && '14px'};
  padding-left: ${props => props.level === 2 && '50px'};
  padding-left: ${props => props.level === 4 && '12px'};
  
  padding-bottom: ${props => props.level === 1 && '10px'};
  padding-bottom: ${props => ( props.isOpen && props.level === 1 ) && '16px'};

  margin-right: 1px;

  border-left: ${props => props.level === 1 && !props.isActive && '3px solid rgba(0,0,0,0);'};
  border-left: ${props => props.level === 1 && props.isActive && '3px solid #0CA0F1'};

  list-style-type: none;
  
  background-color: inherit;
  
  overflow-y: ${props => ( props.level === 0 ) && 'auto'};

  ${props => ( props.level === 1 && ( props.isActive || props.isOpen ) ) && CssSectionActive};

  :hover {
    background-color: ${props => props.level === 1 && '#F9FAFB'};
  }
`;

const SectionLink = styled( Link )`
  text-decoration: none;
`;

const SectionExternalLink = styled.a`
`;

const SectionHeader = styled<{ isActive: boolean, level: number }, 'div'>( 'div' )`
  display: none;
  display: ${props => props.level > 0 && 'inline-flex'};

  margin: 4px 0;

  width: 100%;

  position: relative;

  align-items: center;
  justisfy-content: space-between;
  
  cursor: default;
  cursor: ${props => props.level !== 3 && 'pointer'};

  letter-spacing: ${props => props.level === 2 && '1px;'};
`;

const SectionImage = styled<{ level?: number } & ImageProps>( ( { level = 0, ...rest } ) =>
    <Image {...rest} /> )`
  display: none;
  display: ${props => props.level === 1 && 'inline-block'};

  vertical-align: middle;

  padding-right: 5px;
 
  width: 30px;
  height: 30px;
`;

const SectionExternalLinkImage = styled( Image )`
  display: inline-block;
  margin-left: 5px;
  vertical-align: middle;

  width: 15px;
`;

const SectionMenuArrow = styled<{ isLink: boolean, level: number } & ImageProps>( ( { level = 0, ...rest } ) =>
    <Image {...rest} /> )`
  display: none;
  display: ${props => props.level === 2 && !props.isLink && 'inline-block'};
`;

const SectionTitle = styled<{ isActive: boolean, isLink: boolean, level: number }, 'div'>( 'div' )`
  display: inline-block;

  vertical-align: middle;
  
  width: 148px;
  
  margin-left: ${props => props.level === 1 && '10px'};

  text-decoration: none;
  text-transform:  ${props => props.level === 3 && 'uppercase'};
  letter-spacing:  ${props => props.level === 3 && '1px'}; 
  
  white-space: normal;
  white-space: ${props => props.level === 1 && 'nowrap'};

  font-family: ${props => props.theme.fonts.regular};
  font-family: ${props => ( props.isActive || props.level === 1 ) && props.theme.fonts.medium};

  font-weight: normal;

  font-size: 15px;
  font-size: ${props => props.level === 1 && '15px'};
  font-size: ${props => props.level === 2 && '14px'};
  font-size: ${props => props.level === 3 && '9px'};
  font-size: ${props => props.level === 4 && '14px'};

  color: #62696F;
  color: ${props => props.level === 1 && props.isActive && props.theme.colors.anchorText};

  color: ${props => props.level === 2 && '#808D98'};
  color: ${props => props.level === 2 && props.isActive && props.isLink && props.theme.colors.anchorText};
  color: ${props => props.level === 2 && props.isActive && !props.isLink && props.theme.colors.black};
  color: ${props => props.level === 3 && '#808D98'};
  color: ${props => props.level === 4 && props.isActive && props.theme.colors.anchorText};
  color: ${props => !props.isActive && '#62696F'};

  opacity: 1;
  transition: opacity 0.3s linear;

  :hover {
    color: ${props => ( ( props.level === 2 && props.isLink ) || props.level === 4 ) && props.theme.colors.anchorText};
  }
`;

const SectionChildren = styled<{ isOpen: boolean, level: number }, 'ul'>( 'ul' )`
  overflow: hidden;
  overflow-y: ${props => props.level === 0 && 'auto'};

  margin: 0;
  padding: 0;

  list-style-type: none;
  
  ${props => props.level > 0 && ( props.isOpen ? CssSectionVisible : CssSectionHidden )};
`;

const CssSectionVisible = css`
  max-height: 1500px;
  transition: max-height 0.6s ease-in-out;
`;

const CssSectionHidden = css`
  max-height: 0px;
  transition: max-height 0.4s cubic-bezier(0, 1, 0, 1);
`;

const CssSectionActive = css`
  background-color: #F9FAFB;
  box-shadow: inset -1px 0 2px 0px #EEE;
`;

export default LeftSideMenuSectionComponent;
